// What an approved escalation lets a criteria-checklist item do next.
//
// Owner ruling, 2026-10-26 (#7, option 2): after an approved escalation the
// screen offers what the server allows. The server reads the executive's
// free-text directive with CriteriaChecklistService.ClassifyDirective: a
// directive that names an action pins the item to it, and one that names none
// ("return to pending", a blank one, unmatched text) allows any terminal
// action. This is that classifier's copy; both are pinned by
// tests/fixtures/escalation-directive-cases.json.

import type { CriteriaChecklistItemDto } from "@/api/dealFlow/types";

export type ChecklistTerminalAction = "certify" | "decline" | "waive";

/** The action a directive pins, or "any" when it pins none. */
export type ChecklistDirectiveAction = ChecklistTerminalAction | "any";

/** Mirrors CriteriaChecklistService.ClassifyDirective, arm for arm. */
export function classifyChecklistDirective(
  directive: string | null | undefined,
): ChecklistDirectiveAction {
  if (directive == null || directive.trim() === "") return "any";
  const d = directive.toLowerCase();
  if (d.includes("waiv")) return "waive";
  if (d.includes("decline")) return "decline";
  if (d.includes("return") && d.includes("pending")) return "any";
  if (d.includes("documentation") || d.includes("documents")) return "certify";
  if (d.includes("approve") || d.includes("pass") || d.includes("certify")) return "certify";
  return "any";
}

/**
 * The terminal actions the server accepts on an Escalated item, before the
 * caller's own permissions (`checklistItemActions`) narrow them: none until an
 * executive approves the escalation, then what its directive allows. Empty for
 * an item that is not Escalated.
 */
export function approvedEscalationActions(
  item: CriteriaChecklistItemDto,
): ReadonlySet<ChecklistTerminalAction> {
  const esc = item.activeEscalation;
  if (item.status !== "Escalated" || esc?.status !== "Approved") return new Set();
  const pinned = classifyChecklistDirective(esc.resolutionDirective);
  return new Set<ChecklistTerminalAction>(
    pinned === "any" ? ["certify", "decline", "waive"] : [pinned],
  );
}
